import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useInfiniteQuery, useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, RefreshCw } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, DateTimePicker, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { identifierLabel } from './LabIdentityDialog';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';

const PAGE = 50;
const LABEL_CHUNK = 60;
const LIST_KEY = 'home-collections';

interface HomeCollectionRow {
  id: string;
  status: string;
  attempts?: number;
  address: string;
  windowStart: string | null;
  windowEnd: string | null;
  notes: string | null;
  phlebotomistId: string | null;
  phlebotomistName: string | null;
  assignedAt: string | null;
  collectedAt: string | null;
  failReason: string | null;
  labOrderId: string;
  orderNumber: string;
  priority: string;
  patient: { id: string; mrn: string; fullName: string; phone: string | null };
  tests: { id: string; name: string; specimenType: string; status: string }[];
}

interface HomeCollectionPage {
  data: HomeCollectionRow[];
  nextCursor: string | null;
  enabled?: boolean;
  identityCheck?: boolean;
  identifiers?: string[];
}

interface Phlebotomist {
  id: string;
  name: string;
  phone: string | null;
  designation: string | null;
}

interface AssignResult {
  id: string;
  status: string;
  phlebotomistId: string;
  phlebotomistName: string;
  patientNotified: boolean;
  phlebotomistNotified?: boolean;
}

interface CollectResult {
  id: string;
  status: string;
  collected: { id: string; accessionNo: string }[];
}

const STATUS_FILTERS = [
  { value: '', label: 'Open (waiting or assigned)' },
  { value: 'requested', label: 'Waiting to be assigned' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'collected', label: 'Collected' },
  { value: 'failed', label: 'Visit failed' },
];

const STATUS_LABEL: Record<string, string> = {
  requested: 'Waiting to be assigned',
  assigned: 'Assigned',
  collected: 'Collected',
  failed: 'Visit failed',
};

function statusVariant(status: string): 'warning' | 'default' | 'success' | 'destructive' | 'secondary' {
  if (status === 'requested') return 'warning';
  if (status === 'assigned') return 'default';
  if (status === 'collected') return 'success';
  if (status === 'failed') return 'destructive';
  return 'secondary';
}

function windowText(row: HomeCollectionRow): string {
  if (row.windowStart && row.windowEnd) return `${dateTime(row.windowStart)} to ${dateTime(row.windowEnd)}`;
  if (row.windowStart) return `From ${dateTime(row.windowStart)}`;
  return 'Any time';
}

export function HomeCollectionPage() {
  const { can, user } = useAuth();
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: [LIST_KEY] });
  const [status, setStatus] = useState('');
  const [mine, setMine] = useState(false);
  const [assigning, setAssigning] = useState<HomeCollectionRow | null>(null);
  const [collecting, setCollecting] = useState<HomeCollectionRow | null>(null);
  const [failing, setFailing] = useState<HomeCollectionRow | null>(null);
  const [rescheduling, setRescheduling] = useState<HomeCollectionRow | null>(null);

  const mayAssign = can('lab.homecollection.assign');
  const mayCollect = can('lab.collect');
  const mayFail = can('lab.collect', 'lab.homecollection.assign');

  const query = useInfiniteQuery({
    queryKey: [LIST_KEY, status, mine],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<HomeCollectionPage>(
        `/lab/home-collections${qs({ status, mine: mine ? true : undefined, limit: PAGE, cursor: pageParam })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = query.data?.pages.flatMap((page) => page.data);
  const switchedOff = query.data?.pages[0]?.enabled === false;

  return (
    <>
      <PageHeader
        title={tr('lab', 'home_collection')}
        description={tr('lab', 'sample_collection_visits_at_the_patients_address')}
        actions={
          <Button
            variant="outline"
            loading={fetching > 0}
            onClick={() => void queryClient.invalidateQueries({ queryKey: [LIST_KEY] })}
          >
            {fetching === 0 && <RefreshCw className="h-4 w-4" />} {tr('lab', 'refresh')}
          </Button>
        }
      />

      {switchedOff && (
        <Alert className="mb-3">{tr('lab', 'home_sample_collection_is_switched_off_for_this')}</Alert>
      )}

      <SearchFilterBar
        filters={
          <>
            <FilterField label={tr('lab', 'status')}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-60" aria-label={tr('lab', 'status')}>
                {STATUS_FILTERS.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField><Checkbox checked={mine} onCheckedChange={setMine} label={tr('lab', 'only_my_visits')} /></FilterField>
          </>
        }
        onReset={
          status || mine
            ? () => {
                setStatus('');
                setMine(false);
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={query.isLoading} refreshing={query.isFetching}
        error={query.error}
        rowKey={(row) => row.id}
        empty={mine ? tr('lab', 'none_of_your_visits_match') : tr('lab', 'no_home_collection_visits_match')}
        footer={
          query.hasNextPage ? (
            <div className="flex w-full justify-center">
              <Button variant="outline" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                {tr('lab', 'load_more')}
              </Button>
            </div>
          ) : undefined
        }
        columns={[
          {
            header: tr('lab', 'when_and_where'),
            sort: (row) => row.windowStart,
            cell: (row) => (
              <div className="max-w-xs">
                <p className="font-medium">{windowText(row)}</p>
                <p className="text-xs text-muted-foreground">{row.address}</p>
                {row.notes && <p className="text-xs text-muted-foreground">{tr('lab', 'note')} {row.notes}</p>}
              </div>
            ),
          },
          {
            header: tr('lab', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
                {row.patient.phone ? (
                  <a className="text-xs underline" href={`tel:${row.patient.phone}`}>
                    {row.patient.phone}
                  </a>
                ) : (
                  <span className="text-xs text-muted-foreground">{tr('lab', 'no_phone_on_file')}</span>
                )}
              </div>
            ),
          },
          {
            header: tr('lab', 'order'),
            sort: (row) => row.orderNumber,
            cell: (row) => (
              <div className="max-w-xs">
                <p className="flex items-center gap-2">
                  <span className="font-mono text-xs">{row.orderNumber}</span>
                  {row.priority === 'stat' && <Badge variant="destructive">{tr('lab', 'stat')}</Badge>}
                  {row.priority === 'urgent' && <Badge variant="warning">{tr('lab', 'urgent')}</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {row.tests.length === 0 ? tr('lab', 'no_tests_left_on_the_order') : row.tests.map((test) => test.name).join(', ')}
                </p>
              </div>
            ),
          },
          {
            header: tr('lab', 'phlebotomist'),
            sort: (row) => row.phlebotomistName,
            cell: (row) =>
              row.phlebotomistName ? (
                <div>
                  <p>{row.phlebotomistName}</p>
                  <p className="text-xs text-muted-foreground">{dateTime(row.assignedAt)}</p>
                </div>
              ) : (
                <span className="text-muted-foreground">{tr('lab', 'not_assigned')}</span>
              ),
          },
          {
            header: tr('lab', 'status'),
            sort: (row) => row.status,
            cell: (row) => (
              <div className="space-y-1">
                <Badge variant={statusVariant(row.status)}>{STATUS_LABEL[row.status] ?? row.status}</Badge>
                {row.status === 'collected' && <p className="text-xs text-muted-foreground">{dateTime(row.collectedAt)}</p>}
                {row.status === 'failed' && row.failReason && <p className="text-xs text-muted-foreground">{row.failReason}</p>}
                {(row.attempts ?? 1) > 1 && <p className="text-xs text-muted-foreground">{tr('lab', 'attempt')} {row.attempts}</p>}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => {
              const open = row.status === 'requested' || row.status === 'assigned';
              if (row.status === 'failed' && mayAssign) {
                return (
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => setRescheduling(row)}>
                      {tr('lab', 'reschedule')}
                    </Button>
                  </div>
                );
              }
              if (!open) return null;
              const mayCollectThis = mayCollect && (mayAssign || !row.phlebotomistId || row.phlebotomistId === user?.id);
              return (
                <div className="flex flex-wrap justify-end gap-1">
                  {mayAssign && (
                    <Button size="sm" variant="outline" onClick={() => setAssigning(row)}>
                      {row.phlebotomistId ? tr('lab', 'reassign') : tr('lab', 'assign')}
                    </Button>
                  )}
                  {mayCollectThis && (
                    <Button size="sm" onClick={() => setCollecting(row)}>
                      {tr('lab', 'mark_collected')}
                    </Button>
                  )}
                  {mayFail && (
                    <Button size="sm" variant="ghost" onClick={() => setFailing(row)}>
                      {tr('lab', 'visit_failed')}
                    </Button>
                  )}
                </div>
              );
            },
          },
        ]}
      />

      {assigning && <AssignDialog row={assigning} onClose={() => setAssigning(null)} />}
      {collecting && (
        <CollectDialog
          row={collecting}
          identityCheck={query.data?.pages[0]?.identityCheck === true}
          identifiers={query.data?.pages[0]?.identifiers ?? []}
          onClose={() => setCollecting(null)}
        />
      )}
      {failing && <FailDialog row={failing} onClose={() => setFailing(null)} />}
      {rescheduling && <RescheduleDialog row={rescheduling} onClose={() => setRescheduling(null)} />}
    </>
  );
}

function localToIso(value: string): string | undefined {
  return value ? new Date(value).toISOString() : undefined;
}

function RescheduleDialog({ row, onClose }: { row: HomeCollectionRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const backwards = Boolean(windowStart && windowEnd && new Date(windowEnd) <= new Date(windowStart));

  const save = useMutation({
    mutationFn: () =>
      post<{ patientNotified: boolean }>(`/lab/home-collections/${row.id}/reschedule`, {
        windowStart: localToIso(windowStart),
        windowEnd: localToIso(windowEnd),
        notes: notes.trim() || undefined,
      }),
    onSuccess: (result) => {
      toast.success(tr('lab', 'visit_rescheduled'), result.patientNotified ? tr('lab', 'the_patient_has_been_sent_the_new_window') : tr('lab', 'the_patient_has_no_phone_on_file_so_no_sms_was'));
      void queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'reschedule_the_visit')}
      description={tr('lab', 'x_order_x', { fullName: row.patient.fullName, orderNumber: row.orderNumber })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={backwards || !windowStart}
          >
            {tr('lab', 'reschedule_and_notify_patient')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {row.failReason && <Alert variant="warning">{tr('lab', 'the_last_attempt_failed')} {row.failReason}</Alert>}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={tr('lab', 'window_starts')} required>
            <DateTimePicker className="w-full" value={windowStart} onChange={setWindowStart} />
          </Field>
          <Field label={tr('lab', 'window_ends')} error={backwards ? 'Must be after the start' : undefined}>
            <DateTimePicker className="w-full" value={windowEnd} onChange={setWindowEnd} />
          </Field>
        </div>
        <Field label={tr('lab', 'note_for_the_visit')} hint={tr('lab', 'added_to_the_visit_notes')}>
          <Textarea rows={2} maxLength={400} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function AssignDialog({ row, onClose }: { row: HomeCollectionRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [personId, setPersonId] = useState(row.phlebotomistId ?? '');
  const [error, setError] = useState<string | null>(null);

  const people = useQuery({
    queryKey: ['home-collection-phlebotomists'],
    queryFn: () => get<Phlebotomist[]>('/lab/home-collections/phlebotomists'),
    staleTime: 300_000,
  });

  const save = useMutation({
    mutationFn: () => post<AssignResult>(`/lab/home-collections/${row.id}/assign`, { phlebotomistId: personId }),
    onSuccess: (result) => {
      toast.success(
        tr('lab', 'assigned_to_x', { phlebotomistName: result.phlebotomistName }),
        result.patientNotified
          ? tr('lab', 'the_patient_has_been_sent_an_sms_with_the_visit')
          : tr('lab', 'the_patient_has_no_phone_number_on_file_so_no'),
      );
      void queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'assign_a_phlebotomist')}
      description={tr('lab', 'x_order_x', { fullName: row.patient.fullName, orderNumber: row.orderNumber })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={!personId}
          >
            {tr('lab', 'assign_and_notify_patient')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('lab', 'phlebotomist')} required hint={tr('lab', 'the_patient_is_sent_an_sms_with_this_persons')}>
          <Select value={personId} onChange={(e) => setPersonId(e.target.value)} disabled={people.isLoading}>
            <option value="">{people.isLoading ? tr('lab', 'loading') : tr('lab', 'select')}</option>
            {people.data?.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
                {person.designation ? ` (${person.designation})` : ''}
                {person.phone ? ` · ${person.phone}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        {people.error && <Alert variant="destructive">{describeError(people.error)}</Alert>}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function CollectDialog({
  row,
  identityCheck,
  identifiers,
  onClose,
}: {
  row: HomeCollectionRow;
  identityCheck: boolean;
  identifiers: string[];
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [collected, setCollected] = useState<CollectResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<'wristband_scan' | 'two_identifiers' | 'override'>('two_identifiers');
  const [proof, setProof] = useState('');
  const identityReady = !identityCheck || (method === 'override' ? proof.trim().length >= 5 : proof.trim().length >= 2);
  const identity = identityCheck
    ? {
        patientId: row.patient.id,
        method,
        ...(method === 'wristband_scan' ? { scanned: proof.trim() } : method === 'two_identifiers' ? { value: proof.trim() } : { reason: proof.trim() }),
      }
    : undefined;

  const collect = useMutation({
    mutationFn: () => post<CollectResult>(`/lab/home-collections/${row.id}/collect`, identity ? { identity } : {}),
    onSuccess: (result) => {
      setCollected(result);
      toast.success(tr('lab', 'visit_marked_collected'));
      void queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['lab-worklist'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const printLabels = useMutation({
    mutationFn: async (itemIds: string[]) => {
      for (let start = 0; start < itemIds.length; start += LABEL_CHUNK) {
        const chunk = itemIds.slice(start, start + LABEL_CHUNK);
        const sheet = await post<{ html: string }>('/lab/labels/print', { itemIds: chunk, copies: 1 });
        printDocument(sheet.html);
      }
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (collected) {
    const labelled = collected.collected;
    return (
      <Dialog
        open
        onClose={onClose}
        title={tr('lab', 'specimens_collected')}
        description={tr('lab', 'x_order_x', { fullName: row.patient.fullName, orderNumber: row.orderNumber })}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {labelled.length > 0 ? tr('lab', 'not_now') : tr('lab', 'close')}
            </Button>
            {labelled.length > 0 && (
              <Button
                onClick={() => {
                  setError(null);
                  printLabels.mutate(labelled.map((item) => item.id));
                }}
                loading={printLabels.isPending}
              >
                <Printer className="h-4 w-4" /> {tr('lab', 'print_labels')}
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-3">
          {labelled.length > 0 ? (
            <>
              <p className="text-sm">{tr('lab', 'print_a_label_for_each_tube_now_so_nothing')}</p>
              <ul className="space-y-1 text-sm">
                {labelled.map((item) => (
                  <li key={item.id} className="font-mono text-xs">
                    {item.accessionNo}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm">{tr('lab', 'every_test_on_this_order_already_had_a_specimen')}</p>
          )}
          {error && <Alert variant="destructive">{error}</Alert>}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'mark_this_visit_collected')}
      description={tr('lab', 'x_order_x', { fullName: row.patient.fullName, orderNumber: row.orderNumber })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              collect.mutate();
            }}
            loading={collect.isPending}
            disabled={!identityReady}
          >
            {tr('lab', 'yes_samples_were_collected')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm">
          {tr('lab', 'every_test_still_waiting_on_this_order_is')}
        </p>
        <p className="text-xs text-muted-foreground">{row.tests.map((test) => `${test.name} (${test.specimenType})`).join(', ')}</p>
        {identityCheck && (
          <div className="grid gap-3 rounded-md border p-3">
            <Field label={tr('lab', 'how_was_x_identified', { fullName: row.patient.fullName })}>
              <ChoiceButtons<typeof method>
                layout="cards"
                value={method}
                onChange={(value) => {
                  setMethod(value);
                  setProof('');
                }}
                options={[
                  { value: 'two_identifiers', label: tr('lab', 'asked_a_second_identifier') },
                  { value: 'wristband_scan', label: tr('lab', 'scanned_an_id_card_or_wristband') },
                  ...(can('lab.collect.identity.override') ? [{ value: 'override' as const, label: tr('lab', 'could_not_identify_supervisor') }] : []),
                ]}
              />
            </Field>
            <Field label={method === 'two_identifiers' ? identifierLabel(identifiers) : method === 'override' ? tr('lab', 'why') : tr('lab', 'scanned')}>
              <Input value={proof} onChange={(e) => setProof(e.target.value)} />
            </Field>
          </div>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function FailDialog({ row, onClose }: { row: HomeCollectionRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();

  const save = useMutation({
    mutationFn: () => post(`/lab/home-collections/${row.id}/fail`, { reason: trimmed }),
    onSuccess: () => {
      toast.success(tr('lab', 'visit_recorded_as_failed'));
      void queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'the_visit_did_not_happen')}
      description={tr('lab', 'x_order_x', { fullName: row.patient.fullName, orderNumber: row.orderNumber })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={trimmed.length < 3}
          >
            {tr('lab', 'record_failed_visit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('lab', 'what_happened')} required hint={tr('lab', 'for_example_nobody_home_patient_declined_wrong')}>
          <Textarea value={reason} maxLength={300} rows={3} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
